import { z } from "zod";
import { formFieldSchema, type FormDefinition, type FormField } from "../domain";

// Body of POST /api/forms/:slug/submit. Matches the legacy
// /app/api/forms/[slug]/submit/route.ts inline schema verbatim so the public
// embed contract is preserved. apps/web consumes this same schema in Phase 6
// when the embed-side form validator is rewritten.
export const formSubmitInputSchema = z.object({
  payload: z.record(z.string(), z.unknown()),
  submitterEmail: z.string().trim().email().max(320).optional(),
  submitterName: z.string().trim().max(200).optional(),
  // SEC3: Cloudflare Turnstile token. Verified server-side only when
  // TURNSTILE_SECRET_KEY is configured; otherwise ignored (dev affordance).
  turnstileToken: z.string().max(4096).optional(),
});

export type FormSubmitInput = z.infer<typeof formSubmitInputSchema>;

// ---------------------------------------------------------------------------
// Admin Forms editor — request shapes for /api/admin/forms/*.
//
// The domain (definition, field union, workflow, automation) is reused from
// types/domain.ts via the shared package's re-export — only the request-body
// shapes specific to the admin Hono routes live here. Bucket 6 (portal
// submission) imports formFieldSchema directly from `@colab/shared` to
// validate submissions against the same union.
// ---------------------------------------------------------------------------

// POST /api/admin/forms — the legacy server action accepted FormData with
// name + description; slug is derived server-side from name (with retry on
// collision) and the definition is seeded with one empty section.
export const formCreateRequestSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(160, "Name is too long (max 160 chars)"),
  description: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => (v == null || v === "" ? null : v)),
});
export type FormCreateRequest = z.infer<typeof formCreateRequestSchema>;

// PATCH /api/admin/forms/:id — name/description only. Status transitions
// route through the lifecycle endpoints.
//
// Phase 9.3 — the description field used to carry a
//   .transform((v) => (v == null || v === "" ? null : v))
// which collapsed undefined → null. The route handler then sees
// `parsed.data.description !== undefined` (true, because it is null) and
// forwards `description: null` to updateForm(). updateForm re-parses
// through formUpdateSchema whose description is `optionalText`
// (z.string().optional()), which REJECTS null with "expected string,
// received null". PATCH bodies that only carry { name } would fail. The
// SPA useUpdateFormMeta currently always sends both fields, so this is
// a latent bug — fixed here by dropping the transform so a missing
// description stays undefined and the route's `!== undefined` guard does
// the right thing. Empty-string still collapses to null at this layer.
export const formUpdateMetaRequestSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(160).optional(),
    description: z
      .union([
        z.literal("").transform(() => null),
        z.string().trim().max(2000),
        z.null(),
      ])
      .optional(),
  })
  .refine((v) => v.name !== undefined || v.description !== undefined, {
    message: "At least one field must be patched",
  });
export type FormUpdateMetaRequest = z.infer<typeof formUpdateMetaRequestSchema>;

// POST /api/admin/forms/:id/fields — client-generated field UUID so the
// optimistic id matches the server's; server validates the section exists
// and appends. The full discriminated FormField union is required so kind-
// specific config (select options, file MIME types, etc.) is validated up
// front.
export const formFieldAddRequestSchema = z.object({
  sectionId: z.string().uuid(),
  field: formFieldSchema,
});
export type FormFieldAddRequest = z.infer<typeof formFieldAddRequestSchema>;

// PATCH /api/admin/forms/:id/fields/:fieldId — replace the field in place.
// We send the whole new field rather than a partial because kind switches
// (text → long_text) carry different per-kind config bounds; sending the
// whole field keeps Zod narrowing intact. The route uses :fieldId as the
// identity; body.field.id must match.
export const formFieldUpdateRequestSchema = z.object({
  field: formFieldSchema,
});
export type FormFieldUpdateRequest = z.infer<typeof formFieldUpdateRequestSchema>;

// PATCH /api/admin/forms/:id/fields/order — the whole ordered ID tree, one
// PATCH per drop. Establishes the DnD convention for bucket 11 (journeys
// builder) to inherit: send the entire new ordering rather than per-position
// deltas, so the call is idempotent and re-dispatchable on failure. Sections
// carry their fieldIds so cross-section field moves are expressible.
export const formFieldOrderRequestSchema = z.object({
  sections: z
    .array(
      z.object({
        id: z.string().uuid(),
        fieldIds: z.array(z.string().uuid()),
      }),
    )
    .min(1),
});
export type FormFieldOrderRequest = z.infer<typeof formFieldOrderRequestSchema>;

// ---------------------------------------------------------------------------
// Submission payload validator — built dynamically per form definition.
//
// Relocated from apps/api/src/services/forms.ts in bucket 6 so the worker
// (authenticated portal submit + anonymous embed submit) and the SPA
// (Phase 7 FormRenderer port) can validate submissions against the same
// schema in one import. The worker keeps a re-export shim in services/forms
// so existing call sites are unchanged.
// ---------------------------------------------------------------------------

export function buildSubmissionPayloadValidator(
  def: FormDefinition,
): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const section of def.sections) {
    for (const field of section.fields) {
      if (field.kind === "section_heading") continue;
      shape[field.name] = wrapOptional(buildFieldValidator(field), field);
    }
  }
  return z.object(shape).strict();
}

// Alias for the same function under the brief's vocabulary so a future
// caller can import the name that reads naturally at the call site.
export const buildSubmissionSchema = buildSubmissionPayloadValidator;

function buildFieldValidator(field: FormField): z.ZodTypeAny {
  switch (field.kind) {
    case "text": {
      let s = z.string().trim();
      if (field.maxLength) s = s.max(field.maxLength);
      return field.required ? s.min(1) : s;
    }
    case "long_text": {
      let s = z.string();
      if (field.maxLength) s = s.max(field.maxLength);
      return field.required ? s.min(1) : s;
    }
    case "email": {
      const s = z.string().trim().email().max(320);
      return field.required ? s.min(1, "Required") : s;
    }
    case "number": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let n: any = field.integer ? z.coerce.number().int() : z.coerce.number();
      if (field.min !== undefined) n = n.min(field.min);
      if (field.max !== undefined) n = n.max(field.max);
      return n as z.ZodTypeAny;
    }
    case "date": {
      return z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "Use YYYY-MM-DD",
      });
    }
    case "partner_lookup": {
      return z.string().uuid();
    }
    case "file": {
      return z
        .object({
          url: z.string().url(),
          name: z.string().min(1).max(512),
          size: z.number().int().nonnegative(),
          mimeType: z.string().min(1).max(120),
        })
        .strict();
    }
    case "select": {
      const allowed = field.options.map((o) => o.value);
      if (allowed.length === 0) return z.never();
      const single = z.enum(allowed as [string, ...string[]]);
      if (field.multiple) {
        return z.array(single).min(field.required ? 1 : 0);
      }
      return single;
    }
    case "checkbox": {
      return z.coerce.boolean();
    }
    case "section_heading": {
      return z.undefined();
    }
    default: {
      const _exhaustive: never = field;
      void _exhaustive;
      return z.unknown();
    }
  }
}

function wrapOptional(
  schema: z.ZodTypeAny,
  field: FormField,
): z.ZodTypeAny {
  if (field.kind === "section_heading") return schema;
  if (!field.required) {
    return z.preprocess(
      (v) => (v === "" || v === undefined ? null : v),
      schema.nullable(),
    );
  }
  if (field.kind === "checkbox") return schema;
  return schema;
}

export function validateSubmissionPayload(
  def: FormDefinition,
  payload: unknown,
):
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; issues: z.ZodIssue[] } {
  const validator = buildSubmissionPayloadValidator(def);
  const parsed = validator.safeParse(payload);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues };
  return { ok: true, value: parsed.data as Record<string, unknown> };
}

// ===========================================================================
// Phase 7 sub-bucket 2a — Portal forms runtime: drafts + multi-file
// attachments. Mirrors packages/shared/src/schemas/mdf.ts conventions.
//
// The legacy submitFormPartner action takes a payload + identity and writes a
// single submission row; the new SPA flow inserts a 'draft' row, auto-saves
// payload PATCHes against it, attaches files via the three-step upload, then
// promotes 'draft' → 'pending' on submit. The bucket-6 createSubmission
// pipeline remains the source of truth for downstream acceptance.
// ===========================================================================

// PATCH /api/portal/forms/drafts/:submissionId — partial body the auto-save
// hook posts on every debounced change. The payload is the form-field
// values keyed by field.name; per-field Zod validation runs only on submit
// (matches MDF's "draft fields nullable, submit fields strict" convention).
export const formDraftPatchSchema = z.object({
  payload: z.record(z.string(), z.unknown()).default({}),
});
// Use z.input to match react-hook-form's pre-default-application shape.
export type FormDraftPatchInput = z.input<typeof formDraftPatchSchema>;

// POST /api/portal/forms/drafts/:submissionId/submit — promote draft to
// 'pending'. Body carries optional submitter metadata (the page caller pre-
// populates from the session; embed-equivalent fields kept for parity with
// formSubmitInputSchema). attachment_ids are re-verified server-side against
// the draft's submission_id.
export const formDraftSubmitSchema = z.object({
  submitterEmail: z.string().trim().email().max(320).optional(),
  submitterName: z.string().trim().max(200).optional(),
  attachment_ids: z
    .array(z.string().uuid())
    .max(20, "At most 20 attachments per submission")
    .default([]),
});
export type FormDraftSubmitInput = z.input<typeof formDraftSubmitSchema>;

const FORM_ATTACHMENT_MAX_BYTES = 26_214_400; // 25 MB

export const formAttachmentUploadInitSchema = z.object({
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(FORM_ATTACHMENT_MAX_BYTES),
});
export type FormAttachmentUploadInitInput = z.infer<
  typeof formAttachmentUploadInitSchema
>;

export const formAttachmentFinalizeSchema = z.object({
  submission_id: z.string().uuid(),
  storage_key: z.string().min(1).max(512),
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(FORM_ATTACHMENT_MAX_BYTES),
});
export type FormAttachmentFinalizeInput = z.infer<
  typeof formAttachmentFinalizeSchema
>;

// MIME allowlist for form file fields. Mirrors MDF's list — the legacy
// FileField placeholder said "antivirus / mime allowlist plumbing" was a
// Phase B story; this is the allowlist plumbing.
export const FORM_ATTACHMENT_MIME_ALLOWLIST = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;
export type FormAttachmentMime = (typeof FORM_ATTACHMENT_MIME_ALLOWLIST)[number];

export const FORM_ATTACHMENT_MAX_BYTES_EXPORT = FORM_ATTACHMENT_MAX_BYTES;
